import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ChatInputComponent } from '../components/chat-input/chat-input.component';
import { ChatAttachedFile } from '../components/chat-input/chat-input.interface';
import { ChatPresetSelectComponent } from '../components/chat-preset-select/chat-preset-select.component';
import { ChatShareButtonComponent } from '../components/chat-share/chat-share-button.component';
import { ChatListItemComponent } from '../components/chats-list/chats-list-item/chats-list-item.component';
import { ChatsListComponent } from '../components/chats-list/chats-list.component';
import { ContainerComponent } from '../components/container/container.component';
import { MessagesListComponent } from '../components/messages-list/messages-list.component';
import { MessagesListItemComponent } from '../components/messages-list/messages-list-item/messages-list-item.component';
import { ResizableSplitLayoutComponent } from '../components/resizable-split-layout/resizable-split-layout.component';
import { useRequest } from '../hooks/useRequest';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { refreshChats } from '../store/slices/chats-slice';
import {
  ChatDetail,
  ChatMessage,
  createChatMessage,
  deleteChat,
  fetchChat,
  setChatPreset,
  setToolApproval,
  streamChat,
  uploadChatFile,
} from '../utils/axios/api';
import {
  formatMessageWithEntryContext,
  parseChatEntryContext,
} from '../utils/chat-entry-context/chat-entry-context';
import { ChatMessagesBuilder } from '../utils/chat-messages.builder';
import { ExceptionEnum, extractExceptionCode } from '../utils/exception';
import { getStoredReasoningEffort } from '../utils/reasoning-effort';
import { PendingChatMessage } from './chats.page';

export function ChatPage() {
  const { t } = useTranslation();
  const { id: chatId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const entryContext = useMemo(
    () => parseChatEntryContext(location.search),
    [location.search],
  );
  const chats = useAppSelector((state) => state.chats.items);
  const presets = useAppSelector((state) => state.presets.items);
  const loadRequest = useRequest<ChatDetail>();
  const sendRequest = useRequest();
  const presetRequest = useRequest();
  const approvalRequest = useRequest<ChatMessage[]>();
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<ChatAttachedFile[]>([]);
  const [isChatReady, setIsChatReady] = useState(false);
  const messagesBuilderRef = useRef(ChatMessagesBuilder.empty());
  const abortRef = useRef<AbortController | null>(null);
  const pendingHandledRef = useRef(false);

  useEffect(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setAttachedFiles([]);
    setIsChatReady(false);
    pendingHandledRef.current = false;

    let cancelled = false;

    void loadRequest.fetch(() => fetchChat(chatId)).then((chat) => {
      if (!chat) {
        if (!cancelled) {
          navigate('/chats', { replace: true });
        }
        return;
      }

      if (cancelled) {
        return;
      }

      setSelectedPresetId(chat.presetId);
      messagesBuilderRef.current = ChatMessagesBuilder.from(chat.messages);
      setMessages(messagesBuilderRef.current.build());
      setIsChatReady(true);
    });

    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [chatId, loadRequest, navigate]);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handlePresetChange = useCallback(
    async (presetId: string | null) => {
      setSelectedPresetId(presetId);

      await presetRequest.fetch(async () => {
        await setChatPreset(chatId, presetId);
        void dispatch(refreshChats());
      });
    },
    [chatId, dispatch, presetRequest],
  );

  const handleRemoveFile = useCallback((fileId: string) => {
    setAttachedFiles((prev) => prev.filter((item) => item.id !== fileId));
  }, []);

  const handleDeleteChat = useCallback(
    async (id: string) => {
      await sendRequest.fetch(async () => {
        await deleteChat(id);
        await dispatch(refreshChats()).unwrap();

        if (id === chatId) {
          navigate('/chats');
        }
      });
    },
    [chatId, dispatch, navigate, sendRequest],
  );

  const runStream = useCallback(
    (signal: AbortSignal) =>
      streamChat(
        chatId,
        (event) => {
          if (event.type === 'chat_name') {
            void dispatch(refreshChats());
            return;
          }

          setMessages(messagesBuilderRef.current.apply(event).build());
        },
        signal,
        getStoredReasoningEffort(),
      ),
    [chatId, dispatch],
  );

  const handleSend = useCallback(
    async (text: string, filesToUpload: ChatAttachedFile[] = attachedFiles) => {
      const messageText = formatMessageWithEntryContext(text, entryContext);
      abortRef.current?.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;

      if (filesToUpload === attachedFiles) {
        setAttachedFiles([]);
      }

      const optimisticMessageId = crypto.randomUUID();
      const pendingFiles = filesToUpload.map(({ id, file }) => ({
        id,
        name: file.name,
        mimeType: file.type || undefined,
        isUploading: true,
      }));
      const pendingApprovals = messagesBuilderRef.current
        .build()
        .filter(
          (message) => message.role === 'TOOL' && message.toolApproved === null,
        );

      try {
        if (pendingApprovals.length > 0) {
          messagesBuilderRef.current.syncToolMessages(
            pendingApprovals.map((message) => ({
              ...message,
              toolApproved: false,
            })),
          );
        }

        setMessages(
          messagesBuilderRef.current
            .user({
              id: optimisticMessageId,
              text: messageText,
              files: pendingFiles,
            })
            .build(),
        );

        const result = await sendRequest.fetch(async () => {
          for (const pending of pendingApprovals) {
            if (abortController.signal.aborted) {
              return;
            }

            try {
              const toolMessages = await setToolApproval(
                chatId,
                pending.id,
                false,
              );
              setMessages(
                messagesBuilderRef.current
                  .syncToolMessages(toolMessages)
                  .build(),
              );
            } catch (error) {
              if (
                extractExceptionCode(error) !==
                ExceptionEnum.TOOL_APPROVAL_NOT_PENDING
              ) {
                throw error;
              }
            }
          }

          if (abortController.signal.aborted) {
            return;
          }

          const userMessage = await createChatMessage(chatId, messageText);

          if (abortController.signal.aborted) {
            return;
          }

          messagesBuilderRef.current.replaceMessageId(
            optimisticMessageId,
            userMessage.id,
          );
          setMessages(messagesBuilderRef.current.build());

          if (filesToUpload.length > 0) {
            await Promise.all(
              filesToUpload.map(async ({ id, file }) => {
                const uploaded = await uploadChatFile(userMessage.id, file);

                if (abortController.signal.aborted) {
                  return;
                }

                setMessages(
                  messagesBuilderRef.current
                    .replaceFile(userMessage.id, id, uploaded)
                    .build(),
                );
              }),
            );
          }

          if (abortController.signal.aborted) {
            return;
          }

          await runStream(abortController.signal);
        });

        if (result === null) {
          if (abortController.signal.aborted) {
            return;
          }

          setMessages(
            messagesBuilderRef.current
              .error(t('chatInput.error'))
              .build(),
          );
        }
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }

        setMessages(
          messagesBuilderRef.current
            .error(
              error instanceof Error ? error.message : t('chatInput.error'),
            )
            .build(),
        );
      }
    },
    [attachedFiles, chatId, entryContext, runStream, sendRequest, t],
  );

  const handleToolApproval = useCallback(
    async (messageId: string, approved: boolean) => {
      const toolMessages = await approvalRequest.fetch(() =>
        setToolApproval(chatId, messageId, approved),
      );

      if (!toolMessages) {
        return;
      }

      setMessages(
        messagesBuilderRef.current.syncToolMessages(toolMessages).build(),
      );

      if (toolMessages.some((message) => message.toolApproved === null)) {
        return;
      }

      abortRef.current?.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;

      const result = await sendRequest.fetch(() =>
        runStream(abortController.signal),
      );

      if (result === null && !abortController.signal.aborted) {
        setMessages(
          messagesBuilderRef.current.error(t('chatInput.error')).build(),
        );
      }
    },
    [approvalRequest, chatId, runStream, sendRequest, t],
  );

  useEffect(() => {
    if (!isChatReady || pendingHandledRef.current) {
      return;
    }

    const pending = location.state as PendingChatMessage | null;

    if (!pending?.text) {
      return;
    }

    pendingHandledRef.current = true;
    navigate('.', { replace: true, state: null });
    void handleSend(pending.text, pending.files ?? []);
  }, [handleSend, isChatReady, location.state, navigate]);

  const isAwaitingUserAction = useMemo(
    () =>
      messages.some(
        (message) => message.role === 'TOOL' && message.toolApproved === null,
      ),
    [messages],
  );

  const lastAssistantIndex = useMemo(
    () =>
      messages.reduce(
        (lastIndex, item, itemIndex) =>
          item.role === 'ASSISTANT' ? itemIndex : lastIndex,
        -1,
      ),
    [messages],
  );

  return (
    <ResizableSplitLayoutComponent
      rightHeader={
        <>
          <ChatShareButtonComponent chatId={chatId} />
          <ChatPresetSelectComponent
            presets={presets}
            value={selectedPresetId}
            onChange={(presetId) => {
              void handlePresetChange(presetId);
            }}
            disabled={sendRequest.isLoading}
          />
        </>
      }
      left={
        <ChatsListComponent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="h5">{t('chats.title')}</Typography>
            <Tooltip title={t('chats.new')}>
              <IconButton
                size="small"
                aria-label={t('chats.new')}
                onClick={() => {
                  navigate('/chats');
                }}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {chats.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('chats.empty')}
            </Typography>
          ) : (
            chats.map((chat) => (
              <ChatListItemComponent
                key={chat.id}
                chat={chat}
                selected={chat.id === chatId}
                onClick={() => {
                  navigate(`/chats/${chat.id}`);
                }}
                onDelete={() => {
                  void handleDeleteChat(chat.id);
                }}
              />
            ))
          )}
        </ChatsListComponent>
      }
      right={
        <ContainerComponent>
          <MessagesListComponent key={chatId}>
            {messages.map((message, index) => (
              <MessagesListItemComponent
                key={message.id}
                message={message}
                isStreaming={
                  sendRequest.isLoading &&
                  message.role === 'ASSISTANT' &&
                  index === lastAssistantIndex
                }
                onToolApproval={(approved) => {
                  void handleToolApproval(message.id, approved);
                }}
                isToolApprovalLoading={
                  approvalRequest.isLoading || sendRequest.isLoading
                }
              />
            ))}
          </MessagesListComponent>
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
              flexShrink: 0,
              px: 2,
              pt: 1,
              pb: 2,
              bgcolor: 'background.default',
            }}
          >
            <Box sx={{ width: '100%' }}>
              {attachedFiles.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.75,
                    mb: 1,
                    px: 0.5,
                  }}
                >
                  {attachedFiles.map(({ id, file }) => (
                    <Chip
                      key={id}
                      label={file.name}
                      size="small"
                      onDelete={
                        sendRequest.isLoading
                          ? undefined
                          : () => handleRemoveFile(id)
                      }
                      aria-label={t('chatInput.removeFile', {
                        name: file.name,
                      })}
                    />
                  ))}
                </Box>
              ) : null}
              <ChatInputComponent
                value={attachedFiles}
                onChange={setAttachedFiles}
                onSend={(text) => {
                  void handleSend(text);
                }}
                onStop={handleStop}
                isStreaming={sendRequest.isLoading}
                awaitingUserAction={isAwaitingUserAction}
              />
            </Box>
          </Box>
        </ContainerComponent>
      }
    />
  );
}
